import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Mail, ShieldCheck } from "lucide-react";

import { authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * P5: pagina om een wachtwoord-reset mail aan te vragen.
 *
 * Gedrag bij submit: roept POST /api/auth/wachtwoord-vergeten aan.
 * Server retourneert ALTIJD 204 ongeacht of het email-adres bekend
 * is (geen enumeration). UI toont daarom dezelfde succes-melding —
 * "controleer je inbox" — onafhankelijk van het resultaat.
 */
export function WachtwoordVergetenPagina() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [bezig, setBezig] = useState(false);
  const [verstuurd, setVerstuurd] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBezig(true);
    try {
      await authApi.wachtwoordVergeten(email.trim().toLowerCase());
      setVerstuurd(true);
    } catch {
      // Zelfs bij netwerk-fout tonen we de succes-melding (geen
      // enumeration). Echte fouten gaan via Sentry/logs.
      setVerstuurd(true);
    } finally {
      setBezig(false);
    }
  }

  if (verstuurd) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
        <div className="w-full max-w-sm">
          <div className="rounded-xl border bg-white p-6 space-y-4 text-center">
            <Mail className="h-8 w-8 mx-auto text-brand-cyan-dark" />
            <h1 className="text-[18px] font-semibold">
              {t("auth.reset.mail_onderweg_titel")}
            </h1>
            <p className="text-[13px] text-muted-foreground">
              {t("auth.reset.mail_onderweg_uitleg")}
            </p>
            <Link
              to="/login"
              className="block text-[12.5px] text-brand-cyan-dark hover:underline pt-2"
            >
              {t("auth.terug_naar_login")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="rounded-xl border bg-white p-6 space-y-5">
          <div className="text-center">
            <h1 className="text-[18px] font-semibold">
              {t("auth.reset.titel")}
            </h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              {t("auth.reset.uitleg")}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reset-email">{t("auth.email")}</Label>
              <Input
                id="reset-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jij@voeronline.nl"
              />
            </div>

            <Button type="submit" className="w-full" disabled={bezig || !email}>
              {bezig && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("auth.reset.verstuur")}
            </Button>

            <Link
              to="/login"
              className="block w-full text-center text-[12.5px] text-muted-foreground hover:text-foreground"
            >
              {t("auth.terug_naar_login")}
            </Link>
          </form>
        </div>

        <p className="flex items-center gap-1.5 justify-center text-[12px] text-muted-foreground mt-6">
          <ShieldCheck className="h-3.5 w-3.5" />
          {t("auth.reset.tweefa_blijft_vereist")}
        </p>
      </div>
    </div>
  );
}
